import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X, Sparkles, Brain, PieChart, Ruler, TrendingUp } from 'lucide-react'

const PremiumUpgradeModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation()

  if (!isOpen) return null

  const features = [
    { icon: <Brain className="text-purple-400" />, text: t('premium_modal.feature_ai') },
    { icon: <PieChart className="text-blue-400" />, text: t('premium_modal.feature_charts') },
    { icon: <Ruler className="text-green-400" />, text: t('premium_modal.feature_3d') },
    { icon: <TrendingUp className="text-orange-400" />, text: t('premium_modal.feature_year_trend') },
  ]

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center p-4 overflow-y-auto pt-10 sm:pt-20">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity duration-300" 
        onClick={onClose}
      />
      
      {/* Modal Card */}
      <div className="relative w-full max-w-md bg-[#1c1c1e] rounded-3xl overflow-hidden shadow-2xl border border-white/10 animate-in zoom-in-95 duration-200 mb-10">
        
        {/* Shimmer header background */}
        <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-br from-orange-600/30 to-purple-600/30 blur-3xl -z-10" />
        
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-gray-400 hover:text-white transition-all backdrop-blur-sm z-20"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 pt-10 text-center">
          {/* Header Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-orange-500 blur-2xl opacity-40 animate-pulse" />
              <div className="relative bg-gradient-to-br from-orange-400 to-orange-600 p-4 rounded-2xl shadow-xl">
                <Sparkles className="w-10 h-10 text-black animate-bounce" style={{ animationDuration: '3s' }} />
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-black mb-2 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent italic tracking-tighter">
            {t('premium_modal.title')}
          </h2>
          <p className="text-orange-500 font-bold mb-8 text-sm uppercase tracking-widest bg-orange-500/10 inline-block px-4 py-1 rounded-full border border-orange-500/20">
             {t('premium_modal.subtitle')}
          </p>

          {/* Feature List */}
          <div className="space-y-4 mb-10 text-left">
            {features.map((f, index) => (
              <div key={index} className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5 hover:bg-white/10 transition-colors group">
                <div className="flex-shrink-0 bg-[#2c2c2e] p-2 rounded-lg group-hover:scale-110 transition-transform">
                  {f.icon}
                </div>
                <span className="text-gray-200 font-bold text-sm tracking-tight">{f.text}</span>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4">
            <a 
              href={`/settings?locale=${i18n.language}`}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-black py-5 rounded-2xl shadow-lg shadow-orange-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 text-lg group"
            >
              <span>{t('premium_modal.upgrade_now')}</span>
              <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            </a>
            
            <button 
              onClick={onClose}
              className="py-3 text-gray-500 text-sm font-bold hover:text-gray-300 transition-colors uppercase tracking-widest"
            >
              {t('premium_modal.maybe_later')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default PremiumUpgradeModal
